import {DataTable as PepsicoDsDataTable} from '@pepsico-ds/ui'
import PropTypes from 'prop-types'
import {SIZE} from '@/shared/constants/globalStyle'
import {SELECTION_TYPE} from '@/shared/constants/commonConstants'
import {useEffect, useRef} from 'react'
import {getSelectionType} from '@/components/atoms/DataTable/DataTableUtils'
import {getSelectedRowData} from '@/shared/utils/commons'
import {noop} from '@/shared/utils/noop'

/**
 * DataTable component that renders a PepsicoDsDataTable with properties.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.id=''] - The id of the table. Will be used to generate the id and data-testid.
 * @param {Object[]} props.data - Row data to be rendered in the table. Data keys should match column keys.
 * @param {Object[]} props.columns - Column configuration for the table
 * @param {boolean} [props.isDark=true] - The mode of the table.
 * @param {string} [props.tableHeader=''] - The table heading. Defaults to an empty string.
 * @param {string} props.size - The size of the table. Options: "small" | "medium" | "large".
 * @param {string} props.selection - The selection of the table. Options: "single" | "multiple".
 * @param {number} [props.pageSize=500] - The page size of the table. Defaults to 500.
 * @param {number} [props.pageIndex=0] - The page index of the table. Defaults to 0.
 * @param {Function} [props.onUpdate=() => {}] - Additional effect to be applied when changing pages in the table pagination.
 * @param {string} [props.tableDescription=''] - The table description. Defaults to an empty string.
 * @param {Object[]} props.filters - The filters for the table.
 * @param {Object[]} [props.toolbarActions=[]] - The toolbar actions for the table.
 * @param {Object} [props.rowSelection={}] - Selected row(s) initial state.
 * @param {Function} [props.setRowSelection=() => {}] - Function to update the row selection state
 * @param {string} [props.searchPlaceholder='Search'] - Helper text for the search input. Defaults to 'Search'.
 * @param {boolean} [props.showFilterButton=false] - Show filter button to display filter toolbar.
 * @param {boolean} [props.showFilterToolbar=false] - Prop to show filter toolbar with initial rendering.
 * @param {boolean} [props.isExpandable=false] - Prop to enable expandable rows.
 * @param {Function} [props.renderSubComponent=() => {}] - Function to render the sub component.
 * @param {boolean} [props.showColumnFilters=true] - Prop to show column filters.
 * @param {boolean} [props.enableSorting=true] - Prop to enable sorting.
 * @param {Function} [props.onGlobalSearchChange=() => {}] - Function to handle global search input change.
 * @param {Function} [props.onSortChange=() => {}] - Function to handle column sort change.
 * @param {Function} [props.onColumnSearchChange=() => {}] - Function to handle column search input change.
 * @param {boolean} [props.showGlobalActions=true] - Show global actions bar with search, pagination and filtermenu.
 * @returns {JSX.Element} The rendered PepsicoDsDataTable component.
 */

function DataTable({
  id,
  data,
  columns,
  isDark = true,
  tableHeader = '',
  size = SIZE.SMALL,
  selection = SELECTION_TYPE.MULTIPLE,
  pageSize = 500,
  pageIndex = 0,
  onUpdate = noop,
  tableDescription = '',
  filters = [],
  toolbarActions = [],
  rowSelection = {},
  setRowSelection = noop,
  searchPlaceholder = 'Search',
  showFilterButton = true, //Switch to default false once pepsico-ds package is updated. Current 'true' hides the button and false shows it.
  showFilterToolbar = false,
  setSelectedRowData = noop,
  isExpandable = false,
  renderSubComponent = noop,
  showColumnFilters = true,
  enableSorting = true,
  onGlobalSearchChange = noop,
  onSortChange = noop,
  onColumnSearchChange = noop,
  showGlobalActions = true,
  setPageIndex = noop,
  setSearchArry = noop,
  setSortingObj = noop,
}) {
  const tableRef = useRef(null)
  useEffect(() => {
    if (Object.keys(rowSelection).length > 0) {
      const selectedRowDataArr = getSelectedRowData(rowSelection, data)
      setSelectedRowData(selectedRowDataArr)
    }
  }, [rowSelection, data, setSelectedRowData])

  // Monitor changes to the column search inputs associated with the data table
  useEffect(() => {
    // Guard against invalid columns
    if (!columns?.length) return

    const handleInput = e => {
      // Only handle changes to text input fields within th of the table header elements
      if (e.target.tagName.toLowerCase() === 'input' && e.target.type === 'text' && e.target.closest('th')) {
        const thElement = e.target.closest('th')
        // Get th elements within the same row to correctly determine column index
        const trElement = thElement.closest('tr')
        const thElements = trElement
          ? Array.from(trElement.querySelectorAll('th'))
          : Array.from(tableRef.current.querySelectorAll('th'))
        const columnIndex = thElements.indexOf(thElement)

        // Get the columnKey from the columns array using the index
        const columnKey = columns[columnIndex]?.columnKey

        // Get the search filter value
        const searchFilterValue = e.target.value

        // Instead of logging, call the provided callback
        onColumnSearchChange(columnKey, searchFilterValue)
      }
    }

    // Add input event listener to table element
    const tableElement = tableRef.current
    if (tableElement) {
      tableElement.addEventListener('input', handleInput)
    }

    // Cleanup listener on unmount
    return () => {
      if (tableElement) {
        tableElement.removeEventListener('input', handleInput)
      }
    }
  }, [columns, onColumnSearchChange])

  // Monitor changes to the global table input associated with the data table
  useEffect(() => {
    const handleGlobalSearchInputChange = e => {
      // Only handle changes to search input
      if (e.target.id === 'input-search') {
        onGlobalSearchChange(e.target.value)
      }
    }

    // Add input event listener to table element
    const tableElement = tableRef.current
    if (tableElement) {
      tableElement.addEventListener('input', handleGlobalSearchInputChange)
    }

    // Cleanup listener on unmount
    return () => {
      if (tableElement) {
        tableElement.removeEventListener('input', handleGlobalSearchInputChange)
      }
    }
  }, [onGlobalSearchChange])

  // Monitor Sorting of column headers
  useEffect(() => {
    // Guard against invalid columns
    if (!columns?.length) return

    const handleHeaderClick = e => {
      // Find clicked header container
      const headerContainer = e.target.closest('.header-container')
      if (!headerContainer) return

      // Get all th elements to determine index
      const thElement = headerContainer.closest('th')
      const thElements = Array.from(tableRef.current.querySelectorAll('th'))
      const columnIndex = thElements.indexOf(thElement)

      // Get the columnKey from the columns array using the index
      const columnKey = columns[columnIndex]?.columnKey

      // Check the title attribute to determine sort direction
      const title = headerContainer.getAttribute('title')
      if (title === 'Sort descending') {
        onSortChange(columnKey, 'descending')
      } else if (title === 'Sort ascending') {
        onSortChange(columnKey, 'ascending')
      } else if (title === 'Clear sort') {
        onSortChange(columnKey, 'clear')
      }
    }

    // Add click event listener to table element
    const tableElement = tableRef.current
    if (tableElement) {
      tableElement.addEventListener('click', handleHeaderClick)
    }

    // Cleanup listener on unmount
    return () => {
      if (tableElement) {
        tableElement.removeEventListener('click', handleHeaderClick)
      }
    }
  }, [columns, onSortChange])

  return (
    <div className="rw-datatable" ref={tableRef}>
      <PepsicoDsDataTable
        id={`data_table` + (id ? `_${id}` : '')}
        data-testid={`data_table` + (id ? `_${id}` : '')}
        columns={columns || []}
        data={data || []}
        isDark={isDark}
        pageIndex={pageIndex}
        pageSize={pageSize}
        tableHeader={tableHeader}
        tableDescription={tableDescription}
        selection={getSelectionType(selection)}
        size={size}
        filters={filters}
        onUpdate={onUpdate}
        toolbarActions={toolbarActions}
        rowSelection={rowSelection}
        searchPlaceholder={searchPlaceholder}
        showFilterButton={showFilterButton}
        showFilterToolbar={showFilterToolbar}
        isExpandable={isExpandable}
        renderSubComponent={renderSubComponent}
        showColumnFilters={showColumnFilters}
        enableSorting={enableSorting}
        showGlobalActions={showGlobalActions}
        rowSelectionStateUpdater={setRowSelection}
        onPageIndexChange={e => {
          setPageIndex(e)
        }}
        onColumnFiltersChange={(value, type, name) => {
          setSearchArry({field: name, value, type})
        }}
        sortingHandler={e => {
          setSortingObj(e?.length && e[0])
        }}
      />
    </div>
  )
}

DataTable.propTypes = {
  id: PropTypes.string,
  data: PropTypes.array,
  columns: PropTypes.array,
  isDark: PropTypes.bool,
  tableHeader: PropTypes.string,
  size: PropTypes.string,
  selection: PropTypes.string,
  pageSize: PropTypes.number,
  pageIndex: PropTypes.number,
  onUpdate: PropTypes.func,
  tableDescription: PropTypes.string,
  filters: PropTypes.array,
  toolbarActions: PropTypes.array,
  rowSelection: PropTypes.object,
  setRowSelection: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  showFilterButton: PropTypes.bool,
  showFilterToolbar: PropTypes.bool,
  setSelectedRowData: PropTypes.func,
  isExpandable: PropTypes.bool,
  renderSubComponent: PropTypes.func,
  showColumnFilters: PropTypes.bool,
  enableSorting: PropTypes.bool,
  onGlobalSearchChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onColumnSearchChange: PropTypes.func,
  showGlobalActions: PropTypes.bool,
  setPageIndex: PropTypes.func,
  setSearchArry: PropTypes.func,
  setSortingObj: PropTypes.func,
}

DataTable.defaultProps = {
  id: '',
  data: [],
  columns: [],
  isDark: true,
  tableHeader: '',
  size: SIZE.SMALL,
  selection: SELECTION_TYPE.MULTIPLE,
  pageSize: 500,
  pageIndex: 0,
  onUpdate: noop,
  tableDescription: '',
  filters: [],
  toolbarActions: [],
  rowSelection: {},
  setRowSelection: noop,
  searchPlaceholder: 'Search',
  showFilterButton: true,
  showFilterToolbar: false,
  setSelectedRowData: noop,
  isExpandable: false,
  renderSubComponent: noop,
  showColumnFilters: true,
  enableSorting: true,
  onGlobalSearchChange: noop,
  onSortChange: noop,
  onColumnSearchChange: noop,
  showGlobalActions: true,
  setPageIndex: noop,
  setSearchArry: noop,
  setSortingObj: noop,
}

export default DataTable
